// Builds the normalized `tableController` (the contract `tableExecutor` + `buildTableContext` consume) from a
// page's raw handlers + state accessors. A table page (the Recommendations container) calls this ONCE with
// its bound methods; Otto's executor then drives the grid through this uniform interface, so every table page
// implements the SAME shape. Defensive: a missing dep becomes a no-op (the executor is best-effort), and the
// column-config cloning / pin-toggle reconciliation live HERE (testable) so the page just supplies raw
// handlers. Keep the returned object referentially STABLE across renders (cache it on the page) — Otto's send
// callback depends on it — while the getters read LIVE state each call.
//
// deps:
//   getTableData()  -> { keys: [colKey], config: { [colKey]: { label, isVisible, pinned, ... } } }
//   getSort()       -> { [sortKey]: "asc"|"desc" }   (current order)
//   getPagination() -> { perPage, currentPage, totalPages, totalCount }
//   sortColumn(colKey, direction)  toggle/apply sort on a column
//   hideColumn({ keys, config })   apply a column-visibility config (the container's handleHideColumn)
//   pinColumn(colKey)              TOGGLE pin on a column (the container toggles)
//   unpinAll()
//   setPageSize(n)
//   resetSort()
//   resetLocalFilters()
//   resetTableConfig()
//   exportTable(format, opts)      the page's SIMPLE export ('excel' | 'word' | 'pdf'); opts carries the
//                                  popup's global toggles (includeSoftCost). May return (a promise of)
//                                  { status: "downloaded" | "queued" | "error", message? } so Otto can
//                                  tell the user whether the file downloaded or became a background job.
//   openAdvancedExport()           open the page's own export CHOOSER (Recommendations' ExportPopup), where
//                                  the user picks the export type + its settings. Omit on pages with no
//                                  chooser; `exportCapability.advanced` must agree.
//   exportVariant(key, opts)       run ONE named chooser entry directly (opts = { format, breakdown,
//                                  includeSoftCost }, resolved by the proposal against the variant's own
//                                  declaration). Same outcome contract as exportTable. Omit on pages whose
//                                  chooser entries aren't individually runnable; `exportCapability.variants`
//                                  is only honored when this is wired.
//   exportCapability               { formats: ['excel'], advanced: false, variants: [...] } — what this
//                                  page's export really produces (variants per exportVariantsOf). Omit to
//                                  get EXPORT_CAPABILITY_DEFAULT (simple Excel only).
//   getColumnOptions(colKey)       -> Promise<[{ id?, name }]>  the column's real row-filter options
//   setColumnFilter(colKey, names) apply a discrete option-value row-filter (option NAMES)
//   setColumnWildcard(colKey, { operator, value })  apply a numeric/text threshold row-filter
//   clearColumnFilter(colKey)      clear one column's row-filter
// The last four are page-specific (each grid stores its own filter shape) — a page that doesn't filter in
// place (e.g. Recommendations, which has its own filter panel) simply omits them and column_filter no-ops.
//   setGlobalSearch(text)          the page's global search box (also clears it, called with "")
//   sortRecent(axis)                "created" | "edited" — the grid's own Newly Created/Newly Edited sort
//   gotoColumn(colKey)              scroll to + highlight a column, no data change
//   resetAll()                      the page's single "reset everything" action (filters AND sort)
//   openFilters()                   open the page's filter panel/popup
//   openAppliedFilters()            open the page's "currently applied filters" summary/popup
// These six are all OPTIONAL and each gates its own action in availableActions() — a page that wires none of
// them simply never has search/sort_recent/goto_column/reset_all/open_filters/open_applied_filters offered.
import { buildTableColumns, exportOptionsOf } from "./tableCatalog";

// How long to watch for the page's sort to settle before giving up, and how often to look. Generous:
// the grid re-sorts through setState and often a refetch, and a missed correction is a wrong answer on
// screen, while a late one is merely a second re-sort.
const SORT_SETTLE_MS = 2000;
const SORT_POLL_MS = 100;

const noop = () => {};
const fn = f => (typeof f === "function" ? f : noop);

export const createTableController = (deps = {}) => {
    const d = deps || {};
    const tableData = () => (typeof d.getTableData === "function" ? d.getTableData() || {} : {});
    const configOf = () => tableData().config || {};
    // The page's real export affordances. Most table pages declare NOTHING and inherit the capability's
    // shared default (simple Excel, no chooser) — only a page whose export UI genuinely differs (Recommendations,
    // and later Assets) overrides it. `exportCapability` may be an object or a FUNCTION, so a page whose export
    // depends on live state (a permission check, the current section) stays accurate on a cached controller.
    // Every claim is then reconciled against what the page actually WIRED (advanced needs openAdvancedExport,
    // variants need exportVariant), so a stale or optimistic declaration can never make Otto offer an export
    // that no-ops.
    const exportOptions = () => {
        const declared = typeof d.exportCapability === "function" ? d.exportCapability() : d.exportCapability;
        const opts = exportOptionsOf(declared);
        const advanced = opts.advanced && typeof d.openAdvancedExport === "function";
        const variants = typeof d.exportVariant === "function" ? opts.variants : [];
        return {
            ...opts,
            advanced,
            variants,
            enabled: opts.enabled && (advanced || variants.length > 0 || typeof d.exportTable === "function")
        };
    };
    // The view-shaping actions THIS page actually wired. Declared, not assumed: an action absent here is
    // never proposed, so a missing dep can't become an Otto that says "Done" while nothing happened. ONE
    // definition, referenced from both getContext() and the public method below — never two copies that
    // could disagree.
    const availableActions = () => {
        const has = name => typeof d[name] === "function";
        const out = [];
        if (has("setGlobalSearch")) out.push("search", "clear_search"); // one dep serves both
        if (has("sortRecent")) out.push("sort_recent");
        if (has("gotoColumn")) out.push("goto_column");
        if (has("resetAll")) out.push("reset_all");
        if (has("openFilters")) out.push("open_filters");
        if (has("openAppliedFilters")) out.push("open_applied_filters");
        return out;
    };

    return {
        getContext() {
            const td = tableData();
            const keys = td.keys || Object.keys(td.config || {});
            return {
                columns: buildTableColumns({ columns: keys.map(key => ({ key, ...((td.config || {})[key] || {}) })) }),
                sort: typeof d.getSort === "function" ? d.getSort() : null,
                pagination: typeof d.getPagination === "function" ? d.getPagination() : null,
                // A page can only claim an advanced chooser if it actually supplied one to open.
                exportOptions: exportOptions(),
                availableActions: availableActions()
            };
        },
        /**
         * Sort a column, in a REQUESTED direction.
         *
         * Pages bind their own header-click handler here, and a header click TOGGLES — Recommendations'
         * `updateTableSortFilters(searchKey, val)` cannot even be told a direction (`val` is a maintenance
         * year). So passing "desc" through achieved nothing: the result depended on what the column was
         * already sorted by. On a fresh table the toggle happened to land on descending and the feature
         * looked correct; on a table already sorted descending — which is what the user's own previous
         * action leaves behind — "sort highest first" silently produced ASCENDING while Otto's card
         * reported "(descending) Done ✓". Found 2026-09-10 in a recorded clip.
         *
         * So: fire the page's toggle, watch what it actually produced, and correct it once if it went the
         * wrong way. A page that genuinely honours `direction` lands on it first time and the watch is a
         * no-op. Async, and deliberately not awaited by the executor — the correction lands on its own.
         */
        async sort(colKey, direction) {
            const want = direction === "asc" || direction === "desc" ? direction : null;
            const readDir = () => {
                const current = (typeof d.getSort === "function" && d.getSort()) || {};
                return current ? current[colKey] : undefined;
            };
            const before = readDir();
            fn(d.sortColumn)(colKey, want);
            if (!want) return;

            // The page re-sorts through setState (and usually a refetch), so the new order is not readable
            // on this tick. Watch until it changes, then correct at most once.
            const deadline = Date.now() + SORT_SETTLE_MS;
            while (Date.now() < deadline) {
                // eslint-disable-next-line no-await-in-loop
                await new Promise(resolve => setTimeout(resolve, SORT_POLL_MS));
                const now = readDir();
                if (now === before) continue;
                if (now && now !== want) fn(d.sortColumn)(colKey, want);
                return;
            }
        },
        setColumnVisible(colKey, visible) {
            const td = tableData();
            const config = { ...(td.config || {}) };
            if (!config[colKey]) return; // unknown column — nothing to toggle
            const keys = td.keys || Object.keys(config);
            config[colKey] = { ...config[colKey], isVisible: !!visible };
            fn(d.hideColumn)({ keys, config });
        },
        pin(colKey, on) {
            const current = !!(configOf()[colKey] && configOf()[colKey].pinned);
            if (current === !!on) return; // already in the requested state (the container's handler TOGGLES)
            fn(d.pinColumn)(colKey);
        },
        unpinAll() {
            fn(d.unpinAll)();
        },
        setPageSize(n) {
            fn(d.setPageSize)(n);
        },
        resetSort() {
            fn(d.resetSort)();
        },
        resetLocalFilters() {
            fn(d.resetLocalFilters)();
        },
        resetTableConfig() {
            fn(d.resetTableConfig)();
        },
        // Run the page's export. `advanced` opens the page's own chooser (the user picks the type + settings
        // there, so there is no file outcome to report — "opened"); `simple` runs the one-click export and
        // passes its outcome straight through, defaulting to "downloaded" for a page that reports nothing.
        // `opts` carries the popup's global toggle(s) (today: includeSoftCost) — pages that don't take opts
        // simply ignore the extra argument.
        export(format, mode, opts) {
            if (mode === "advanced" && typeof d.openAdvancedExport === "function") {
                d.openAdvancedExport();
                return { status: "opened" };
            }
            if (typeof d.exportTable !== "function") return { status: "error", message: "This page has no export." };
            return Promise.resolve(d.exportTable(format, opts || {})).then(r => (r && r.status ? r : { status: "downloaded" }));
        },
        // Run one of the page's NAMED export variants (a chooser entry — Normal Grouping / CrossWalk /
        // Criticality-Based on Recommendations). opts = { format, breakdown, includeSoftCost }, already
        // resolved and grounded by the proposal against the variant's own declaration. Outcomes as export().
        exportVariant(key, opts) {
            if (typeof d.exportVariant !== "function") return { status: "error", message: "This page has no such export." };
            return Promise.resolve(d.exportVariant(key, opts || {})).then(r => (r && r.status ? r : { status: "downloaded" }));
        },
        // Row filtering (column_filter). Page-provided: each grid owns its filter param shape, so the page
        // supplies these bound handlers (createTableController stays page-agnostic). Missing dep -> no-op /
        // empty options, so a page that doesn't filter in place degrades cleanly.
        getColumnOptions(colKey) {
            if (typeof d.getColumnOptions !== "function") return Promise.resolve([]);
            try {
                return Promise.resolve(d.getColumnOptions(colKey)).then(r => (Array.isArray(r) ? r : [])).catch(() => []);
            } catch (e) {
                return Promise.resolve([]);
            }
        },
        setColumnFilter(colKey, names) {
            fn(d.setColumnFilter)(colKey, Array.isArray(names) ? names : [names].filter(v => v != null));
        },
        setColumnWildcard(colKey, spec) {
            fn(d.setColumnWildcard)(colKey, spec || {});
        },
        clearColumnFilter(colKey) {
            fn(d.clearColumnFilter)(colKey);
        },
        // Which view-shaping actions this page wired — the same list getContext() reports. Exposed as a
        // public method too: the executor (and Task 11's binding test) call it directly on the controller.
        availableActions,
        search(text) {
            fn(d.setGlobalSearch)(text == null ? "" : String(text));
        },
        sortRecent(axis) {
            fn(d.sortRecent)(axis === "edited" ? "edited" : "created");
        },
        gotoColumn(colKey) {
            fn(d.gotoColumn)(colKey);
        },
        resetAll() {
            fn(d.resetAll)();
        },
        openFilters() {
            fn(d.openFilters)();
        },
        openAppliedFilters() {
            fn(d.openAppliedFilters)();
        }
    };
};
